import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button } from '@/shared/components/Button';
import { Select } from '@/shared/components/Select';
import { Input } from '@/shared/components/Input';
import { Textarea } from '@/shared/components/Textarea';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { Badge } from '@/shared/components/Badge';
import { ApiError } from '@/lib/api-client';
import { useClassAttendance, useMarkAttendance } from '../hooks/useProfessorClasses';
import { useClassDescription, useSetClassDescription } from '../hooks/useClassMaterials';
const STATUS_OPTIONS = [
    { value: 'present', label: 'Presente' },
    { value: 'absent', label: 'Ausente' },
    { value: 'late', label: 'Retraso' },
    { value: 'justified', label: 'Justificada' },
];
/**
 * Contadores VIVOS del formulario (lo que se guardará al pulsar el botón).
 * Antes reflejaban el estado guardado en servidor y contradecían a las filas
 * ("12 sin marcar" con las 12 filas en Presente).
 */
function AttendanceSummary({ rows }) {
    const count = (status) => rows.filter((r) => r.status === status).length;
    const items = [
        { label: 'presentes', value: count('present') },
        { label: 'ausentes', value: count('absent') },
        { label: 'retrasos', value: count('late') },
        { label: 'justificadas', value: count('justified') },
    ];
    return (_jsxs("p", { className: "text-xs text-gray-500 dark:text-gray-400", children: [rows.length, " alumnos \u00B7", ' ', items.map((i, idx) => (_jsxs("span", { children: [idx > 0 && ' · ', _jsxs("span", { className: i.value > 0 ? 'font-medium text-gray-700 dark:text-gray-300' : '', children: [i.value, " ", i.label] })] }, i.label)))] }));
}
export function AttendanceMarkForm({ classId, onSuccess, onDirtyChange }) {
    const [serverError, setServerError] = useState('');
    const [rows, setRows] = useState([]);
    const [initialized, setInitialized] = useState(false);
    const [touched, setTouched] = useState(false);
    const { data, isError, isFetchedAfterMount, refetch } = useClassAttendance(classId);
    const students = data?.eligible_students ?? [];
    const markAttendance = useMarkAttendance(classId);
    // "Lo dado en clase" se edita y guarda JUNTO con la asistencia (paridad con
    // el móvil; antes solo vivía en el modal de Material y se descartaba al cerrar).
    const description = useClassDescription(classId);
    const setDescription = useSetClassDescription(classId);
    const [descText, setDescText] = useState('');
    const [descSeeded, setDescSeeded] = useState(false);
    if (!descSeeded && description.data !== undefined) {
        setDescSeeded(true);
        setDescText(description.data.description ?? '');
    }
    useEffect(() => {
        onDirtyChange?.(touched);
    }, [touched, onDirtyChange]);
    // Sembrar UNA vez y SOLO desde la respuesta del servidor de este montaje
    // (refetchOnMount 'always' en el hook). Sembrar desde la caché re-enviaba un
    // estado pre-guardado al re-abrir el modal: revertía la lista y borraba notas.
    if (!initialized && isFetchedAfterMount && students.length > 0) {
        setInitialized(true);
        setRows(students.map((s) => ({
            student_id: s.id,
            student_name: s.display_name,
            shift_name: s.shift_name ?? null,
            change_type: s.change_type ?? null,
            no_longer_eligible: s.no_longer_eligible ?? false,
            status: s.current_attendance?.status ?? 'present',
            notes: s.current_attendance?.notes ?? '',
        })));
    }
    function setAllPresent() {
        setTouched(true);
        // No tocar el histórico de quien ya no pertenece a la clase.
        setRows((prev) => prev.map((r) => (r.no_longer_eligible ? r : { ...r, status: 'present' })));
    }
    function updateRow(studentId, field, value) {
        setTouched(true);
        setRows((prev) => prev.map((r) => (r.student_id === studentId ? { ...r, [field]: value } : r)));
    }
    async function handleSubmit(e) {
        e.preventDefault();
        setServerError('');
        // Descripción sucia → guardarla junto con la asistencia. Si falla, abortamos
        // aquí (la asistencia aún no se ha enviado: nada queda a medias).
        if (descText.trim() !== (description.data?.description ?? '').trim()) {
            try {
                await setDescription.mutateAsync(descText.trim());
            }
            catch (err) {
                setServerError(err instanceof ApiError ? err.message : 'No se pudo guardar la descripción.');
                return;
            }
        }
        markAttendance.mutate({
            records: rows.map((r) => {
                const trimmed = r.notes.trim();
                return {
                    student_id: r.student_id,
                    status: r.status,
                    // El campo muestra la nota guardada: vaciarlo es borrado deliberado
                    // (null). Omitir la clave significaría "preservar" en el backend.
                    notes: trimmed === '' ? null : trimmed,
                };
            }),
        }, {
            onSuccess: () => {
                setTouched(false);
                onSuccess();
            },
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Error al guardar la asistencia.');
            },
        });
    }
    if (isError) {
        return (_jsxs("div", { className: "flex flex-col items-center gap-3 py-8", children: [_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: "No se pudo cargar la lista de la clase." }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", onClick: () => void refetch(), children: "Reintentar" })] }));
    }
    // Hasta tener la respuesta fresca del servidor (no la caché), spinner:
    // el estado editable se siembra de ella.
    if (!isFetchedAfterMount) {
        return (_jsx("div", { className: "flex h-48 items-center justify-center", children: _jsx(Spinner, { size: "lg" }) }));
    }
    if (students.length === 0) {
        return _jsx(EmptyState, { icon: "users", title: "No hay alumnos para esta clase" });
    }
    return (_jsxs("form", { onSubmit: (e) => void handleSubmit(e), className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("label", { htmlFor: "class-description", className: "text-sm font-medium text-gray-700 dark:text-gray-300", children: "Lo dado en clase" }), _jsx(Textarea, { id: "class-description", value: descText, maxLength: 2000, rows: 2, placeholder: "Describe brevemente el temario impartido\u2026", onChange: (e) => {
                            setTouched(true);
                            setDescText(e.target.value);
                        } })] }), _jsxs("div", { className: "flex items-center justify-between gap-3", children: [_jsx(AttendanceSummary, { rows: rows }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", onClick: setAllPresent, children: "Marcar todos como presente" })] }), _jsx("div", { className: "flex flex-col divide-y divide-gray-200 dark:divide-gray-700", children: rows.map((row) => (_jsxs("div", { className: "grid grid-cols-1 items-center gap-3 py-3 md:grid-cols-2", children: [_jsxs("p", { className: "flex flex-wrap items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100", children: [row.student_name, row.change_type === 'shift_change' && (_jsx(Badge, { variant: "info", children: row.shift_name ? `Otro turno · ${row.shift_name}` : 'Otro turno' })), row.change_type === 'single_class' && _jsx(Badge, { variant: "success", children: "Cambio puntual" }), row.no_longer_eligible && _jsx(Badge, { variant: "warning", children: "Ya no en la clase" }), row.change_type === null && row.shift_name && (_jsx("span", { className: "rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-normal text-gray-500 dark:bg-gray-800 dark:text-gray-400", children: row.shift_name }))] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(Select, { options: STATUS_OPTIONS, value: row.status, onChange: (e) => updateRow(row.student_id, 'status', e.target.value) }), _jsx(Input, { placeholder: "Notas (opcional)", value: row.notes, maxLength: 500, onChange: (e) => updateRow(row.student_id, 'notes', e.target.value) })] })] }, row.student_id))) }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx("div", { className: "flex justify-end gap-3", children: _jsx(Button, { type: "submit", loading: markAttendance.isPending || setDescription.isPending, children: "Guardar asistencia" }) })] }));
}
//# sourceMappingURL=AttendanceMarkForm.js.map